import type { Ohaeng } from '../types/index';
import type { FourPillars } from '../types/index';
import type { YongSinResult, DayMasterStrength, JeonwangPattern } from '../types/index';
import { getStemElement, getElementRelation } from '../core/five-elements';
import { findHanSin, ALL_ELEMENTS } from './yong-sin';

/**
 * 병약법 (病藥法) — 사주의 병(과다/부족 패턴)을 진단하고 치료 오행 처방
 */

const EXCESS_THRESHOLD = 2.5;

/** 전왕 판정 기준 비율 */
const JEONWANG_THRESHOLD = 0.7;

interface CategoryElements {
  bigyeop: Ohaeng;
  inseong: Ohaeng;
  siksang: Ohaeng;
  jaeseong: Ohaeng;
  gwansal: Ohaeng;
}

interface DiagnosisResult {
  pattern: string;
  yongSin: Ohaeng;
  huiSin: Ohaeng;
  applicability: number;
  description: string;
  jeonwangPattern?: JeonwangPattern;
}

/**
 * 병약법에 의한 용신 판단
 *
 * 우선순위: 재다신약 → 관다신약 → 상관견관 → 인다무재 → 범용
 */
export function determineByeongyakYongSin(
  fourPillars: FourPillars,
  ohaengDistribution: Record<Ohaeng, number>,
  strength: DayMasterStrength,
): YongSinResult {
  const dayMasterElement = getStemElement(fourPillars.day.stem);
  const rel = getElementRelation(dayMasterElement);

  const bigyeop = dayMasterElement;
  const inseong = rel.generatedBy;
  const siksang = rel.generates;
  const jaeseong = rel.overcomes;
  const gwansal = rel.overcomeBy;

  const isWeak = ['극약', '태약', '신약', '중화신약'].includes(strength.level);

  const diagnosis = diagnose(ohaengDistribution, isWeak, bigyeop, inseong, siksang, jaeseong, gwansal);

  const yongRel = getElementRelation(diagnosis.yongSin);
  const giSin = yongRel.overcomeBy;
  const huiRel = getElementRelation(diagnosis.huiSin);
  const guSin = huiRel.overcomeBy;
  const hanSin = findHanSin(diagnosis.yongSin, diagnosis.huiSin, giSin, guSin);

  return {
    method: '병약',
    yongSin: diagnosis.yongSin,
    huiSin: diagnosis.huiSin,
    giSin,
    guSin,
    hanSin,
    applicability: diagnosis.applicability,
    description: diagnosis.description,
    jeonwangPattern: diagnosis.jeonwangPattern,
  };
}

/** 전왕 패턴 탐지: 특정 카테고리가 전체 오행분포의 70%+ 차지 */
function detectJeonwang(
  dist: Record<Ohaeng, number>,
  cat: CategoryElements,
): JeonwangPattern | undefined {
  const total = ALL_ELEMENTS.reduce((sum, el) => sum + dist[el], 0);
  if (total <= 0) return undefined;

  const categoryRatios: [JeonwangPattern, number][] = [
    ['비겁전왕', (dist[cat.bigyeop] + dist[cat.inseong]) / total],
    ['식상전왕', dist[cat.siksang] / total],
    ['재성전왕', dist[cat.jaeseong] / total],
    ['관성전왕', dist[cat.gwansal] / total],
    ['인성전왕', dist[cat.inseong] / total],
  ];

  for (const [pattern, ratio] of categoryRatios) {
    if (ratio >= JEONWANG_THRESHOLD) return pattern;
  }
  return undefined;
}

/**
 * 전왕 패턴에 맞는 종격 용신을 반환한다.
 * 전왕 = 기세를 따르는 것이 원칙 (종격과 동일 논리)
 */
function getJeonwangDiagnosis(
  pattern: JeonwangPattern,
  cat: CategoryElements,
  dist: Record<Ohaeng, number>,
): DiagnosisResult {
  const dominant = (() => {
    switch (pattern) {
      case '비겁전왕': return cat.bigyeop;
      case '식상전왕': return cat.siksang;
      case '재성전왕': return cat.jaeseong;
      case '관성전왕': return cat.gwansal;
      case '인성전왕': return cat.inseong;
    }
  })();

  // 종격 용신: 기세를 따름 (순용=dominant, 역용=dominant를 생하는 오행)
  const yongSin = dominant;
  const huiSin = getElementRelation(dominant).generatedBy;
  const total = ALL_ELEMENTS.reduce((sum, el) => sum + dist[el], 0);
  const ratio = ((dist[dominant] + (pattern === '비겁전왕' ? dist[cat.inseong] : 0)) / total * 100).toFixed(0);

  return {
    pattern: pattern,
    yongSin,
    huiSin,
    applicability: 0.8,
    description: `${pattern}: ${dominant} 오행이 ${ratio}%로 전왕, 기세를 따라 ${yongSin}이 용신`,
    jeonwangPattern: pattern,
  };
}

function diagnose(
  dist: Record<Ohaeng, number>,
  isWeak: boolean,
  bigyeop: Ohaeng,
  inseong: Ohaeng,
  siksang: Ohaeng,
  jaeseong: Ohaeng,
  gwansal: Ohaeng,
): DiagnosisResult {
  const cat: CategoryElements = { bigyeop, inseong, siksang, jaeseong, gwansal };
  const jeonwangPattern = detectJeonwang(dist, cat);

  // 전왕 감지 시 종격 용신 우선 반환 (기세를 따름)
  if (jeonwangPattern) {
    return getJeonwangDiagnosis(jeonwangPattern, cat, dist);
  }

  let result: DiagnosisResult;

  if (isWeak && dist[jaeseong] >= EXCESS_THRESHOLD) {
    result = {
      pattern: '재다신약',
      yongSin: bigyeop,
      huiSin: inseong,
      applicability: Math.min(1.0, 0.7 + (dist[jaeseong] - EXCESS_THRESHOLD) * 0.15),
      description: `재다신약(財多身弱): 재성(${jaeseong}) ${dist[jaeseong].toFixed(1)}로 과다, 비겁(${bigyeop})이 약`,
    };
  } else if (isWeak && dist[gwansal] >= EXCESS_THRESHOLD) {
    result = {
      pattern: '관다신약',
      yongSin: siksang,
      huiSin: bigyeop,
      applicability: Math.min(1.0, 0.7 + (dist[gwansal] - EXCESS_THRESHOLD) * 0.15),
      description: `관다신약(官多身弱): 관살(${gwansal}) ${dist[gwansal].toFixed(1)}로 과다, 식상(${siksang})으로 제어`,
    };
  } else if (dist[siksang] >= EXCESS_THRESHOLD && dist[gwansal] >= EXCESS_THRESHOLD) {
    result = {
      pattern: '상관견관',
      yongSin: inseong,
      huiSin: bigyeop,
      applicability: Math.min(1.0, 0.7 + (Math.min(dist[siksang], dist[gwansal]) - EXCESS_THRESHOLD) * 0.15),
      description: `상관견관(傷官見官): 식상(${siksang}) ${dist[siksang].toFixed(1)}과 관살(${gwansal}) ${dist[gwansal].toFixed(1)} 동시 과다, 인성(${inseong})으로 중재`,
    };
  } else if (dist[inseong] >= EXCESS_THRESHOLD && dist[jaeseong] < 1.0) {
    result = {
      pattern: '인다무재',
      yongSin: jaeseong,
      huiSin: siksang,
      applicability: Math.min(1.0, 0.7 + (dist[inseong] - EXCESS_THRESHOLD) * 0.15),
      description: `인다무재(印多無財): 인성(${inseong}) ${dist[inseong].toFixed(1)}로 과다, 재성(${jaeseong})이 부족`,
    };
  } else {
    const maxElement = ALL_ELEMENTS.reduce((a, b) => dist[a] > dist[b] ? a : b);
    const maxRel = getElementRelation(maxElement);
    const medicine = maxRel.overcomeBy;
    result = {
      pattern: '범용',
      yongSin: medicine,
      huiSin: getElementRelation(medicine).generatedBy,
      applicability: dist[maxElement] >= EXCESS_THRESHOLD ? 0.4 : 0.2,
      description: `범용 병약: ${maxElement}(${dist[maxElement].toFixed(1)})이 가장 과다, ${medicine}으로 극제`,
    };
  }

  return result;
}
